package com.evoapps.evoshift.ui.roster

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Check
import androidx.compose.material.icons.filled.EventAvailable
import androidx.compose.material.icons.filled.Groups
import androidx.compose.material.icons.filled.Layers
import androidx.compose.material.icons.filled.NotificationsActive
import androidx.compose.material.icons.filled.Publish
import androidx.compose.material3.Button
import androidx.compose.material3.OutlinedButton
import androidx.compose.foundation.BorderStroke
import com.evoapps.evoshift.domain.usecase.todo.AvailabilityStep
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.domain.usecase.todo.RosterProgress
import com.evoapps.evoshift.domain.usecase.todo.RosterStage
import com.evoapps.evoshift.ui.DeepNavy
import com.evoapps.evoshift.ui.adaptive.handCursor
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.dashboard_request_availability
import evoshift.composeapp.generated.resources.first_steps_done
import evoshift.composeapp.generated.resources.roster_skeleton_open_finish
import evoshift.composeapp.generated.resources.todo_reminded
import evoshift.composeapp.generated.resources.todo_remind
import evoshift.composeapp.generated.resources.tracker_all_filled
import evoshift.composeapp.generated.resources.tracker_answers
import evoshift.composeapp.generated.resources.tracker_assign
import evoshift.composeapp.generated.resources.tracker_current_step
import evoshift.composeapp.generated.resources.tracker_not_asked
import evoshift.composeapp.generated.resources.tracker_publish
import evoshift.composeapp.generated.resources.tracker_ready
import evoshift.composeapp.generated.resources.tracker_shifts
import evoshift.composeapp.generated.resources.tracker_step_assignment
import evoshift.composeapp.generated.resources.tracker_step_availability
import evoshift.composeapp.generated.resources.tracker_step_demand
import evoshift.composeapp.generated.resources.tracker_step_publication
import evoshift.composeapp.generated.resources.tracker_unfilled
import org.jetbrains.compose.resources.pluralStringResource
import org.jetbrains.compose.resources.stringResource

/** SH-170: test tags of a roster card's tracker and of its step rows (by [RosterStage]). */
fun rosterTrackerTag(rosterId: String) = "roster_tracker_$rosterId"
fun rosterTrackerActionTag(rosterId: String) = "roster_tracker_action_$rosterId"
fun rosterTrackerStepTag(rosterId: String, stage: RosterStage) = "roster_tracker_${rosterId}_${stage.name}"
/** SH-172: the availability's button beside the demand's (ask, or remind) while both are open. */
fun rosterTrackerAlongsideTag(rosterId: String) = "roster_tracker_alongside_$rosterId"

private val DoneGreen = Color(0xFF2E7D32)

/** What the tracker's one button does; null: not offered (a role that does not build rosters). */
class RosterTrackerActions(
    /** Demand: the skeleton step (approve the month's shifts). */
    val openSkeleton: (() -> Unit)? = null,
    val requestAvailability: (() -> Unit)? = null,
    /** "Przypomnij": those who have not answered. */
    val remind: (() -> Unit)? = null,
    /** Assignments and publication: the roster builder (auto-assign, publish). */
    val openBuilder: (() -> Unit)? = null
)

/**
 * SH-170: a roster card's four steps (Zapotrzebowanie → Dyspozycyjność → Przydziały →
 * Publikacja), each with its numbers, the current one highlighted, and ONE button for it.
 * SH-172: the first two in either order: each shows its own state (availability asked and
 * answered before the demand is ticked), and while the demand is built the availability keeps
 * its own button beside it (ask the team, or remind them).
 */
@Composable
fun RosterTracker(
    progress: RosterProgress,
    actions: RosterTrackerActions,
    reminded: Boolean,
    modifier: Modifier = Modifier
) {
    val labels = mapOf(
        RosterStage.DEMAND to stringResource(Res.string.tracker_step_demand),
        RosterStage.AVAILABILITY to stringResource(Res.string.tracker_step_availability),
        RosterStage.ASSIGNMENT to stringResource(Res.string.tracker_step_assignment),
        RosterStage.PUBLICATION to stringResource(Res.string.tracker_step_publication)
    )
    val current = labels[progress.stage]
    val currentText = current?.let { stringResource(Res.string.tracker_current_step, it) }
    Column(
        modifier.fillMaxWidth().testTag(rosterTrackerTag(progress.rosterId)).semantics(mergeDescendants = false) {
            currentText?.let { contentDescription = it }
        },
        verticalArrangement = Arrangement.spacedBy(4.dp)
    ) {
        labels.entries.forEachIndexed { index, (stage, label) ->
            StepRow(
                number = index + 1,
                label = label,
                figure = figureOf(stage, progress),
                state = stepState(stage, progress),
                modifier = Modifier.testTag(rosterTrackerStepTag(progress.rosterId, stage))
            )
        }
        TrackerButton(progress, actions, reminded, Modifier.testTag(rosterTrackerActionTag(progress.rosterId)))
        AlongsideButton(progress, actions, reminded, Modifier.testTag(rosterTrackerAlongsideTag(progress.rosterId)))
    }
}

/** Done, the step the button acts on, under way beside it (SH-172: answers coming in), or later. */
private enum class StepState { DONE, CURRENT, IN_PROGRESS, LATER }

/** SH-172: each of the first two steps by its own state; the last two in order. */
private fun stepState(stage: RosterStage, progress: RosterProgress): StepState = when (stage) {
    RosterStage.DEMAND -> when {
        progress.demandDone -> StepState.DONE
        progress.stage == RosterStage.DEMAND -> StepState.CURRENT
        else -> StepState.LATER
    }
    RosterStage.AVAILABILITY -> when {
        progress.stage == RosterStage.AVAILABILITY -> StepState.CURRENT
        progress.availability == AvailabilityStep.DONE || progress.availability == AvailabilityStep.SKIPPED -> StepState.DONE
        progress.availability == AvailabilityStep.WAITING -> StepState.IN_PROGRESS
        else -> StepState.LATER
    }
    else -> when {
        progress.stage.ordinal > stage.ordinal -> StepState.DONE
        progress.stage == stage -> StepState.CURRENT
        else -> StepState.LATER
    }
}

@Composable
private fun figureOf(stage: RosterStage, progress: RosterProgress): String? = when (stage) {
    RosterStage.DEMAND -> stringResource(Res.string.tracker_shifts, progress.shifts)
    RosterStage.AVAILABILITY -> when {
        progress.answered != null && progress.team != null -> stringResource(Res.string.tracker_answers, progress.answered!!, progress.team!!)
        progress.availabilitySkipped -> stringResource(Res.string.tracker_not_asked)
        else -> null
    }
    RosterStage.ASSIGNMENT ->
        if (progress.stage.ordinal < RosterStage.AVAILABILITY.ordinal) null
        else if (progress.unfilled > 0) pluralStringResource(Res.plurals.tracker_unfilled, progress.unfilled, progress.unfilled)
        else stringResource(Res.string.tracker_all_filled)
    RosterStage.PUBLICATION -> if (progress.stage == RosterStage.PUBLICATION) stringResource(Res.string.tracker_ready) else null
    RosterStage.DONE -> null
}

@Composable
private fun StepRow(number: Int, label: String, figure: String?, state: StepState, modifier: Modifier = Modifier) {
    val current = state == StepState.CURRENT
    Row(
        modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(8.dp))
            .background(if (current) DeepNavy.copy(alpha = 0.07f) else Color.Transparent)
            .padding(horizontal = 8.dp, vertical = 4.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        val circle = Modifier.size(22.dp).clip(CircleShape)
        when (state) {
            StepState.DONE -> Box(circle.background(DoneGreen), contentAlignment = Alignment.Center) {
                Icon(Icons.Default.Check, contentDescription = stringResource(Res.string.first_steps_done), tint = Color.White, modifier = Modifier.size(14.dp))
            }
            StepState.CURRENT -> Box(circle.background(DeepNavy), contentAlignment = Alignment.Center) {
                Text("$number", style = MaterialTheme.typography.labelMedium, fontWeight = FontWeight.Bold, color = Color.White)
            }
            StepState.IN_PROGRESS -> Box(circle.border(2.dp, DeepNavy, CircleShape), contentAlignment = Alignment.Center) {
                Text("$number", style = MaterialTheme.typography.labelMedium, fontWeight = FontWeight.Bold, color = DeepNavy)
            }
            StepState.LATER -> Box(circle.border(1.5.dp, DeepNavy.copy(alpha = 0.45f), CircleShape), contentAlignment = Alignment.Center) {
                Text("$number", style = MaterialTheme.typography.labelMedium, color = DeepNavy.copy(alpha = 0.75f))
            }
        }
        Spacer(Modifier.width(10.dp))
        Text(
            label,
            style = MaterialTheme.typography.bodyMedium,
            fontWeight = if (current) FontWeight.Bold else FontWeight.Normal,
            color = if (state == StepState.LATER) DeepNavy.copy(alpha = 0.75f) else DeepNavy,
            modifier = Modifier.weight(1f)
        )
        figure?.let {
            Text(
                it,
                style = MaterialTheme.typography.bodySmall,
                fontWeight = if (current) FontWeight.Bold else FontWeight.Normal,
                color = DeepNavy.copy(alpha = if (current) 1f else 0.75f)
            )
        }
    }
}

/**
 * SH-172: while the demand is the step, the availability's own button beside it: ask the team
 * (nobody asked yet) or remind those who have not answered; the reminder sent, said.
 */
@Composable
private fun AlongsideButton(progress: RosterProgress, actions: RosterTrackerActions, reminded: Boolean, tag: Modifier) {
    val (icon, label, action) = when (progress.availabilityAlongside) {
        AvailabilityStep.NOT_ASKED -> Triple(Icons.Default.EventAvailable, stringResource(Res.string.dashboard_request_availability), actions.requestAvailability)
        AvailabilityStep.WAITING -> when {
            reminded -> Triple(Icons.Default.Check, stringResource(Res.string.todo_reminded), null)
            progress.remindable.isNotEmpty() -> Triple(
                Icons.Default.NotificationsActive,
                pluralStringResource(Res.plurals.todo_remind, progress.remindable.size, progress.remindable.size),
                actions.remind
            )
            else -> return
        }
        else -> return
    }
    if (action == null) {
        if (reminded) {
            Row(Modifier.padding(start = 8.dp, top = 4.dp), verticalAlignment = Alignment.CenterVertically) {
                Icon(icon, null, tint = DoneGreen, modifier = Modifier.size(18.dp))
                Spacer(Modifier.width(8.dp))
                Text(label, style = MaterialTheme.typography.labelLarge, color = DoneGreen, fontWeight = FontWeight.Bold)
            }
        }
        return
    }
    OutlinedButton(
        onClick = action,
        modifier = Modifier.fillMaxWidth().handCursor().then(tag),
        shape = RoundedCornerShape(8.dp),
        border = BorderStroke(1.dp, DeepNavy),
        colors = ButtonDefaults.outlinedButtonColors(contentColor = DeepNavy),
        contentPadding = PaddingValues(vertical = 8.dp)
    ) {
        Icon(icon, null, modifier = Modifier.size(18.dp))
        Spacer(Modifier.width(8.dp))
        Text(label, fontWeight = FontWeight.Bold)
    }
}

@Composable
private fun TrackerButton(progress: RosterProgress, actions: RosterTrackerActions, reminded: Boolean, tag: Modifier) {
    val (icon, label, action) = when (progress.stage) {
        RosterStage.DEMAND -> Triple(Icons.Default.Layers, stringResource(Res.string.roster_skeleton_open_finish), actions.openSkeleton)
        RosterStage.AVAILABILITY -> when {
            !progress.requestSent -> Triple(Icons.Default.EventAvailable, stringResource(Res.string.dashboard_request_availability), actions.requestAvailability)
            reminded -> Triple(Icons.Default.Check, stringResource(Res.string.todo_reminded), null)
            progress.remindable.isNotEmpty() -> Triple(
                Icons.Default.NotificationsActive,
                pluralStringResource(Res.plurals.todo_remind, progress.remindable.size, progress.remindable.size),
                actions.remind
            )
            else -> Triple(Icons.Default.Groups, stringResource(Res.string.tracker_assign), actions.openBuilder)
        }
        RosterStage.ASSIGNMENT -> Triple(Icons.Default.Groups, stringResource(Res.string.tracker_assign), actions.openBuilder)
        RosterStage.PUBLICATION -> Triple(Icons.Default.Publish, stringResource(Res.string.tracker_publish), actions.openBuilder)
        RosterStage.DONE -> Triple<ImageVector?, String?, (() -> Unit)?>(null, null, null)
    }
    if (label == null || icon == null) return
    if (action == null) {
        // Done already (the reminder went): said, not offered.
        if (reminded) {
            Row(Modifier.padding(start = 8.dp, top = 4.dp), verticalAlignment = Alignment.CenterVertically) {
                Icon(icon, null, tint = DoneGreen, modifier = Modifier.size(18.dp))
                Spacer(Modifier.width(8.dp))
                Text(label, style = MaterialTheme.typography.labelLarge, color = DoneGreen, fontWeight = FontWeight.Bold)
            }
        }
        return
    }
    Spacer(Modifier.size(4.dp))
    Button(
        onClick = action,
        modifier = Modifier.fillMaxWidth().handCursor().then(tag),
        colors = ButtonDefaults.buttonColors(containerColor = DeepNavy, contentColor = Color.White),
        shape = RoundedCornerShape(8.dp),
        contentPadding = PaddingValues(vertical = 8.dp)
    ) {
        Icon(icon, null, modifier = Modifier.size(18.dp))
        Spacer(Modifier.width(8.dp))
        Text(label, fontWeight = FontWeight.Bold)
    }
}
